import React from 'preact/compat';
import ReactDOM from 'preact/compat';

import ChatWidget, { type WidgetPosition } from './ChatWidget';

import './ChatWidget.css';
// Import color variables - Vite handles CSS natively!
import '../styles/colors.css';

const VALID_POSITIONS: WidgetPosition[] = [
  'bottom-right',
  'bottom-left',
  'top-right',
  'top-left',
];

interface WidgetConfig {
  apiKey: string;
  bubbleColor?: string;
  position?: WidgetPosition;
  greeting?: string;
  defaultOpen?: boolean;
}

const getScriptParams = (): URLSearchParams | null => {
  const script = document.currentScript as HTMLScriptElement | null;
  if (script?.src) {
    return new URL(script.src).searchParams;
  }
  const scripts = document.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i++) {
    const s = scripts[i] as HTMLScriptElement;
    if (s.src && s.src.includes('circuit-chat-widget')) {
      return new URL(s.src).searchParams;
    }
  }
  return null;
};

const getWidgetConfig = (): WidgetConfig | null => {
  let params: URLSearchParams;

  if (import.meta.env.DEV) {
    params = new URLSearchParams(window.location.search);
    if (!params.get('apiKey')) {
      const fallbackKey = import.meta.env.AGENT_API_KEY;
      if (fallbackKey) {
        console.log('[Dev Mode] No apiKey in URL, using default test key');
        params.set('apiKey', fallbackKey);
      }
    } else {
      console.log('[Dev Mode] Using apiKey from URL:', params.get('apiKey'));
    }
  } else {
    const scriptParams = getScriptParams();
    if (!scriptParams) return null;
    params = scriptParams;
  }

  const apiKey = params.get('apiKey');
  if (!apiKey) return null;

  const position = params.get('position') as WidgetPosition | null;

  return {
    apiKey,
    bubbleColor: params.get('color') ?? undefined,
    position:
      position && VALID_POSITIONS.includes(position) ? position : undefined,
    greeting: params.get('greeting') ?? undefined,
    defaultOpen: params.get('open') === 'true',
  };
};

const config = getWidgetConfig();

if (!config) {
  console.error(
    'Circuit Chat Widget: apiKey query parameter is missing in the script tag.',
  );
} else {
  const widgetContainer = document.createElement('div');
  widgetContainer.id = 'circuit-chat-widget-container';
  widgetContainer.className = 'radix-themes';
  document.body.appendChild(widgetContainer);

  ReactDOM.render(
    <React.StrictMode>
      <ChatWidget
        apiKey={config.apiKey}
        bubbleColor={config.bubbleColor}
        position={config.position}
        greeting={config.greeting}
        defaultOpen={config.defaultOpen}
      />
    </React.StrictMode>,
    widgetContainer,
  );
}
